import { createContext, type ReactNode, useContext, useMemo } from 'react';
import {
  type BuildSeriesOptions,
  buildSeries,
  SERIES_COLORS,
  type SeriesSet,
} from './series';

export interface ChartBrand {
  primary: string;
  secondary?: string;
}

const ChartBrandContext = createContext<ChartBrand | undefined>(undefined);

/**
 * Supplies the tenant's brand colours to every chart below it. Without a
 * provider, charts fall back to the neutral default palette.
 */
export function ChartBrandProvider({
  primary,
  secondary,
  children,
}: ChartBrand & { children: ReactNode }) {
  const value = useMemo(() => ({ primary, secondary }), [primary, secondary]);
  return (
    <ChartBrandContext.Provider value={value}>
      {children}
    </ChartBrandContext.Provider>
  );
}

export function useChartBrand(): ChartBrand | undefined {
  return useContext(ChartBrandContext);
}

/** The single accent colour for one-series marks: the brand's primary. */
export function useAccentColor(): string {
  return useChartBrand()?.primary ?? SERIES_COLORS[0];
}

/**
 * `buildSeries` with the brand from context, memoised on the rows. The
 * accessors are expected to be pure, so they are not dependencies.
 */
export function useSeries<R>(
  rows: readonly R[],
  name: (row: R) => string | null | undefined,
  value: (row: R) => number,
  { max, otherLabel }: Omit<BuildSeriesOptions, 'brand'> = {},
): SeriesSet {
  const brand = useChartBrand();
  // biome-ignore lint/correctness/useExhaustiveDependencies: accessors are pure; re-running on their identity would rebuild every render.
  return useMemo(
    () => buildSeries(rows, name, value, { max, otherLabel, brand }),
    [rows, brand, max, otherLabel],
  );
}
